import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Button,
  Input,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useToast,
  useColorModeValue,
  useBreakpointValue,
  Text,
  FormControl,
  FormLabel,
  Switch,
  Badge,
  Select,
} from "@chakra-ui/react";
import { MdEdit, MdDelete, MdAdd, MdDone, MdCancel } from "react-icons/md";
import Card from "components/card/Card";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import CreateHoliday from "./CreateHolidays";
import { useTranslation } from "react-i18next";

const HolidaysList = () => {
  const { t } = useTranslation();
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredHolidays, setFilteredHolidays] = useState([]);
  const [editableHoliday, setEditableHoliday] = useState(null);
  const toast = useToast();
  const [showCreateHolidayModal, setShowCreateHolidayModal] = useState(false);
  const [showEditHolidayModal, setShowEditHolidayModal] = useState(false);
  const modalSize = useBreakpointValue({ base: "full", md: "xl" });
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const pageId = 24;

  // Permissions (replace with real permission logic if needed)
  const [permissions, setPermissions] = useState({
    canList: true,
    canEdit: true,
    canDelete: true,
    canInsert: true,
  });

  // Fetch holidays
  useEffect(() => {
    const fetchHolidays = async () => {
      setLoading(true);
      try {
        const response = await fetchWithToken(
          `${process.env.REACT_APP_API_URL}/holidays?page_id=${pageId}`
        );
        if (response?.status === "success") {
          setHolidays(response.data);
        } else {
          toast({
            title: t("Error"),
            description: response?.message || t("Failed to fetch holidays"),
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top-right",
          });
        }
      } catch (error) {
        toast({
          title: t("Error"),
          description: t("Failed to fetch holidays"),
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      } finally {
        setLoading(false);
      }
    };
    fetchHolidays();
  }, [showCreateHolidayModal, showEditHolidayModal, t, toast]);

  // Search filter
  useEffect(() => {
    const filtered = holidays.filter((holiday) => {
      const name = holiday.name ? holiday.name.toLowerCase() : "";
      const country = holiday.country_code ? holiday.country_code.toLowerCase() : "";
      const date = holiday.date ? holiday.date : "";
      const searchTermLower = searchTerm.toLowerCase();
      return (
        name.includes(searchTermLower) ||
        country.includes(searchTermLower) ||
        date.includes(searchTermLower)
      );
    });
    setFilteredHolidays(filtered);
  }, [searchTerm, holidays]);

  // Edit button handler
  const handleEditHoliday = (holiday) => {
    setEditableHoliday({ ...holiday });
    setShowEditHolidayModal(true);
  };

  // Save handler
  const handleSaveHoliday = async () => {
    try {
      const data = {
        id: editableHoliday.id,
        date: editableHoliday.date,
        country_code: editableHoliday.country_code.toUpperCase(),
        name: editableHoliday.name,
        is_active: editableHoliday.is_active,
      };
      const response = await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/holidays?page_id=${pageId}`,
        "PATCH",
        data
      );
      if (!response || response.status !== "success") {
        throw new Error(response?.message || "Failed to update holiday");
      }
      setShowEditHolidayModal(false);
      setEditableHoliday(null);
      toast({
        title: t("Success"),
        description: t("Holiday updated successfully."),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: t("Error updating holiday."),
        description: error.message || t("Failed to update holiday"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Delete handler
  const handleDeleteHoliday = async (holidayId) => {
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/holidays/delete?page_id=${pageId}`,
        "DELETE",
        { id: holidayId }
      );
      if (!response || response.status !== "success") {
        throw new Error(response?.message || "Failed to delete holiday");
      }
      setHolidays((prev) => prev.filter((h) => h.id !== holidayId));
      toast({
        title: t("Success"),
        description: t("Holiday deleted successfully."),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: t("Error deleting holiday."),
        description: error.message || t("Failed to delete holiday"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Active status toggle
  const handleToggleActive = async (holiday) => {
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/holidays/active?page_id=${pageId}`,
        "PATCH",
        { id: holiday.id, is_active: !holiday.is_active }
      );
      if (!response || response.status !== "success") {
        throw new Error(response?.message || "Failed to update status");
      }
      setHolidays((prev) =>
        prev.map((h) =>
          h.id === holiday.id ? { ...h, is_active: !h.is_active } : h
        )
      );
      toast({
        title: t("Success"),
        description: t("Holiday status updated."),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: t("Error updating status."),
        description: error.message || t("Failed to update status"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Card direction="column" w="100%" px="0px" overflowX={{ sm: "scroll", lg: "hidden" }} boxShadow="lg">
      <Box
        overflowY="auto"
        maxHeight="400px"
        px="25px"
        mb="20px"
        display="flex"
        flexDirection={{ base: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ base: "flex-start", md: "center" }}
      >
        <Flex alignItems="center" mb={{ base: 4, md: 0 }}>
          <Text color="gray.800" fontSize={{ base: "18px", md: "22px" }} fontWeight="700" lineHeight="100%" mb={{ base: "10px", md: "0" }}>
            {t("Holidays List")}
          </Text>
          <Button
            onClick={() => setShowCreateHolidayModal(true)}
            isDisabled={!permissions.canInsert}
            mb={1}
            ml={4}
            colorScheme="cyan"
            color="white"
            borderRadius="full"
            boxShadow="sm"
            _hover={{ bg: "cyan.600", transform: "scale(1.05)" }}
            _active={{ bg: "cyan.700", transform: "scale(0.95)" }}
            transition="all 0.2s"
          >
            <MdAdd />
          </Button>
        </Flex>
        <Input
          placeholder={t("Search by name, country, or date")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="sm"
          w={{ base: "100%", md: "300px" }}
        />
      </Box>
      <Box >
        <Box overflowY="auto" maxHeight="400px" >
          {loading ? (
            <Spinner size="xl" />
          ) : (
                   <Table variant="simple" color="gray.500" mb="24px">
                         <Thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                             <Tr bg='rgb(69, 50, 240)' textColor="white" style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                  <Th textColor="white">{t("Index")}</Th>
                  <Th textColor="white">{t("Date")}</Th>
                  <Th textColor="white">{t("Country Code")}</Th>
                  <Th textColor="white">{t("Name")}</Th>
                  <Th textColor="white">{t("Active")}</Th>
                  <Th textColor="white">{t("Actions")}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredHolidays.map((holiday, index) => (
                  <Tr key={holiday.id}>
                    <Td>{index + 1}</Td>
                    <Td>{holiday.date}</Td>
                    <Td>{holiday.country_code}</Td>
                    <Td>{holiday.name}</Td>
                    <Td>
                      <Switch
                        isChecked={holiday.is_active}
                        onChange={() => handleToggleActive(holiday)}
                        colorScheme="cyan"
                        mr={2}
                        isDisabled={!permissions.canEdit}
                      />
                      <Badge
                        colorScheme={holiday.is_active ? "green" : "red"}
                        variant="solid"
                        px={2}
                        py={1}
                        borderRadius="md"
                        fontSize="0.85em"
                      >
                        {holiday.is_active ? t("Active") : t("Inactive")}
                      </Badge>
                    </Td>
                    <Td>
                      <Flex gap={2}>
                        <Button
                          colorScheme="cyan"
                          size="sm"
                          isDisabled={!permissions.canEdit}
                          color={"white"}
                          onClick={() => handleEditHoliday(holiday)}
                          leftIcon={<MdEdit />}
                        >
                          {t("Edit")}
                        </Button>
                        <Button
                          colorScheme="red"
                          size="sm"
                          isDisabled={!permissions.canDelete}
                          onClick={() => handleDeleteHoliday(holiday.id)}
                          leftIcon={<MdDelete />}
                        >
                          {t("Delete")}
                        </Button>
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Box>
      </Box>
      {/* Create Holiday Modal */}
      <Modal isOpen={showCreateHolidayModal} onClose={() => setShowCreateHolidayModal(false)} size={modalSize}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Text color={textColor} fontSize="2xl" fontWeight="700">
              {t("Create Holiday")}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <CreateHoliday />
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="red" onClick={() => setShowCreateHolidayModal(false)}>
              {t("Close")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* Edit Holiday Modal */}
      <Modal isOpen={showEditHolidayModal} onClose={() => setShowEditHolidayModal(false)} size={modalSize}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Text color={textColor} fontSize="3xl" fontWeight="700">
              {t("Edit Holiday")}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {editableHoliday && (
              <Box as="form">
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="500" mb="8px" color="blue.600">
                    {t("Date")}
                  </FormLabel>
                  <Input
                    isRequired
                    type="date"
                    mb="24px"
                    size="lg"
                    value={editableHoliday.date}
                    onChange={(e) =>
                      setEditableHoliday({
                        ...editableHoliday,
                        date: e.target.value,
                      })
                    }
                  />
                 <FormLabel fontSize="sm" fontWeight="500" mb="8px" color="blue.600">
  {t("Country Code")}
</FormLabel>
<Select
  isRequired
  mb="24px"
  size="lg"
  value={editableHoliday.country_code}
  onChange={(e) =>
    setEditableHoliday({
      ...editableHoliday,
      country_code: e.target.value,
    })
  }
>
  <option value="IT">Italy</option>
</Select>
                  <FormLabel fontSize="sm" fontWeight="500" mb="8px" color="blue.600">
                    {t("Holiday Name")}
                  </FormLabel>
                  <Input
                    isRequired
                    type="text"
                    mb="24px"
                    size="lg"
                    value={editableHoliday.name}
                    onChange={(e) =>
                      setEditableHoliday({
                        ...editableHoliday,
                        name: e.target.value,
                      })
                    }
                  />
                  <FormLabel fontSize="sm" fontWeight="500" mb="8px" color="blue.600">
                    {t("Active")}
                  </FormLabel>
                  <Switch
                    isChecked={editableHoliday.is_active}
                    onChange={e =>
                      setEditableHoliday({
                        ...editableHoliday,
                        is_active: e.target.checked,
                      })
                    }
                    colorScheme="cyan"
                    mr={2}
                  />
                  <Badge
                    colorScheme={editableHoliday.is_active ? "green" : "red"}
                    variant="solid"
                    px={2}
                    py={1}
                    borderRadius="md"
                    fontSize="0.85em"
                  >
                    {editableHoliday.is_active ? t("Active") : t("Inactive")}
                  </Badge>
                </FormControl>
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="cyan" mr={3} onClick={handleSaveHoliday} leftIcon={<MdDone />}>
              {t("Save")}
            </Button>
            <Button colorScheme="red" onClick={() => setShowEditHolidayModal(false)}>
              {t("Cancel")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  );
};

export default HolidaysList;